/**
 * JE preview table, allocation summary, write-off checkbox, and action buttons.
 */

import { Button } from '@elsapiens/ui'
import { cn } from '@elsapiens/utils'

import { formatAmount, toSmallestUnit } from '../../utils/formatters'

import type { PreviewLine } from './record-payment-helpers'

interface RecordPaymentPreviewProps {
  previewLines: PreviewLine[]
  previewBalance: { totalDebit: number; totalCredit: number; isBalanced: boolean }
  baseCurrency: string
  invoiceCurrency: string
  unallocatedAmount: number
  shouldShowWriteOff: boolean
  shouldWriteOffRemaining: boolean
  onWriteOffRemainingChange: (v: boolean) => void
  remainingAfterPayment: number
  isSaving: boolean
  paymentAmount: number | null
  advanceApplied: number | null
  onClose: () => void
  onSubmit: () => void
}

interface JePreviewTableProps {
  previewLines: PreviewLine[]
  previewBalance: { totalDebit: number; totalCredit: number; isBalanced: boolean }
  baseCurrency: string
}

function PreviewLineRow({ line, baseCurrency }: { line: PreviewLine; baseCurrency: string }): JSX.Element {
  const typeClass = line.type === 'debit'
    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
    : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
  return (
    <tr className="hover:bg-muted">
      <td className="el-py-xs">
        <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', typeClass)}>
          {line.type === 'debit' ? 'DR' : 'CR'}
        </span>
      </td>
      <td className="el-py-xs"><span className="font-mono text-xs text-primary mr-2">{line.accountCode}</span>{line.accountName}</td>
      <td className="el-py-xs text-xs text-foreground-secondary">{line.memo}</td>
      <td className="el-py-xs text-right font-mono">{formatAmount(toSmallestUnit(line.amount, line.currency), line.currency)}</td>
      <td className="el-py-xs text-right font-mono text-foreground-secondary">
        {line.currency !== baseCurrency
          ? `≈ ${formatAmount(toSmallestUnit(line.baseAmount, baseCurrency), baseCurrency)}`
          : formatAmount(toSmallestUnit(line.baseAmount, baseCurrency), baseCurrency)}
      </td>
    </tr>
  )
}

function JePreviewTable({ previewLines, previewBalance, baseCurrency }: JePreviewTableProps): JSX.Element {
  const balanceDiff = Math.abs(previewBalance.totalDebit - previewBalance.totalCredit)
  const balanceClass = previewBalance.isBalanced ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
  const wideStyle = { marginInline: 'calc(var(--el-container-px) * -0.5)', width: 'calc(100% + var(--el-container-px))' }
  return (
    <div className="card overflow-hidden" style={wideStyle}>
      <div className="el-px-sm el-py-xs bg-muted border-b border-border">
        <h3 className="text-sm font-semibold text-foreground-secondary">Journal Entry Preview</h3>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-foreground-secondary border-b border-border">
            <th className="el-py-xs text-left w-16">Type</th>
            <th className="el-py-xs text-left">Account</th>
            <th className="el-py-xs text-left">Memo</th>
            <th className="el-py-xs text-right">Amount</th>
            <th className="el-py-xs text-right">Base ({baseCurrency})</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {previewLines.map((line, i) => <PreviewLineRow key={i} baseCurrency={baseCurrency} line={line} />)}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-border font-medium">
            <td className="el-py-xs text-right" colSpan={4}>Balance:</td>
            <td className="el-py-xs text-right font-mono">
              <span className={balanceClass}>
                {formatAmount(toSmallestUnit(balanceDiff, baseCurrency), baseCurrency)}
                {previewBalance.isBalanced ? ' ✓' : ' (unbalanced)'}
              </span>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function WriteOffCheckbox({ remainingAfterPayment, invoiceCurrency, shouldWriteOffRemaining, onWriteOffRemainingChange }: {
  remainingAfterPayment: number; invoiceCurrency: string; shouldWriteOffRemaining: boolean; onWriteOffRemainingChange: (v: boolean) => void
}): JSX.Element {
  return (
    <label className="flex items-start gap-3 rounded-lg border border-border bg-muted el-p-sm cursor-pointer">
      <input checked={shouldWriteOffRemaining} className="w-4 h-4 mt-0.5 rounded border-border text-primary focus:ring-ring" name="write-off-remaining" type="checkbox" onChange={e => onWriteOffRemainingChange(e.target.checked)} />
      <span className="sr-only">Write off remaining balance</span>
      <div className="text-sm">
        <span className="font-medium text-foreground">Write off remaining {formatAmount(toSmallestUnit(remainingAfterPayment, invoiceCurrency), invoiceCurrency)}</span>
        <span className="text-foreground-secondary ml-1">as a rounding adjustment (DR 9910 Rounding Off)</span>
      </div>
    </label>
  )
}

export function RecordPaymentPreview({
  previewLines, previewBalance, baseCurrency, invoiceCurrency, unallocatedAmount,
  shouldShowWriteOff, shouldWriteOffRemaining, onWriteOffRemainingChange,
  remainingAfterPayment, isSaving, paymentAmount, advanceApplied, onClose, onSubmit,
}: RecordPaymentPreviewProps): JSX.Element {
  const isSubmitDisabled = isSaving || ((paymentAmount ?? 0) <= 0 && (advanceApplied ?? 0) <= 0)
  return (
    <>
      {unallocatedAmount > 0 && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/30 el-p-sm text-sm text-amber-800 dark:text-amber-200">
          Amount exceeds outstanding balance. The excess of{' '}
          <span className="font-mono font-medium">{formatAmount(toSmallestUnit(unallocatedAmount, invoiceCurrency), invoiceCurrency)}</span>{' '}
          will be recorded as a customer advance.
        </div>
      )}
      {shouldShowWriteOff && (
        <WriteOffCheckbox invoiceCurrency={invoiceCurrency} remainingAfterPayment={remainingAfterPayment} shouldWriteOffRemaining={shouldWriteOffRemaining} onWriteOffRemainingChange={onWriteOffRemainingChange} />
      )}
      {previewLines.length > 0 && (
        <JePreviewTable baseCurrency={baseCurrency} previewBalance={previewBalance} previewLines={previewLines} />
      )}
      <div className="flex justify-end el-gap-field">
        <Button disabled={isSaving} variant="ghost" onClick={onClose}>Cancel</Button>
        <Button disabled={isSubmitDisabled} onClick={onSubmit}>{isSaving ? 'Saving...' : 'Record & Post'}</Button>
      </div>
    </>
  )
}
